import { Link } from "react-router-dom";
import { Boxes, BookOpenCheck, Cpu, KeyRound, Layers, ShieldCheck, Sparkles, Hash, Coins, Trophy, ShoppingBag, Flame } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-foreground bg-grid">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-20 md:grid-cols-12 md:py-28">
          <div className="md:col-span-7">
            <div className="mono mb-6 inline-flex items-center gap-2 border border-foreground bg-background px-3 py-1.5 text-[10px] uppercase tracking-widest">
              <span className="h-1.5 w-1.5 rounded-full bg-accent"></span>
              Pan-African Academic Blockchain Network
            </div>
            <h1 className="font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
              Learning that is{" "}
              <span className="bg-foreground px-2 text-background">verifiable.</span>
              <br />
              Credentials that are{" "}
              <span className="border-b-[6px] border-primary">immutable.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground/80 md:text-lg">
              Growthink AI-Cademy is a decentralised AI-powered educational
              operating system — uniting an LMS, blockchain certification, an AI
              tutor, and a lifelong skills passport into a single trust layer
              for learners, institutions and employers.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/login">
                <Button
                  size="lg"
                  className="rounded-none bg-foreground text-background hard-shadow hover-lift"
                  data-testid="hero-cta-start"
                >
                  Enter the Academy →
                </Button>
              </Link>
              <Link to="/verify">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-none border-foreground hard-shadow-sm hover-lift"
                  data-testid="hero-cta-verify"
                >
                  <ShieldCheck className="mr-2 h-4 w-4" /> Verify a Certificate
                </Button>
              </Link>
            </div>

            <div className="mono mt-10 grid grid-cols-3 gap-4 border-t border-foreground/20 pt-6 text-xs uppercase tracking-widest">
              <div>
                <div className="text-2xl font-bold text-foreground">12</div>
                <div className="mt-1 text-muted-foreground">Full programmes</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">3</div>
                <div className="mt-1 text-muted-foreground">LLM providers</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">∞</div>
                <div className="mt-1 text-muted-foreground">Audit trail</div>
              </div>
            </div>
          </div>

          {/* Hero side block — certificate visual */}
          <div className="md:col-span-5">
            <div className="relative">
              <div className="cert-grain border-2 border-foreground bg-white p-6 hard-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">
                      Certificate · NFT-backed
                    </div>
                    <div className="font-display mt-1 text-xl font-extrabold tracking-tighter">
                      Blockchain Fundamentals
                    </div>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center bg-foreground text-background">
                    <Boxes className="h-5 w-5" />
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3 border-t border-foreground/15 pt-4 text-xs">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Awarded to
                    </div>
                    <div className="mt-1 font-semibold">Aria Mokoena</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Network
                    </div>
                    <div className="mono mt-1">polygon · sim</div>
                  </div>
                </div>

                <div className="mt-4 break-all border border-foreground/20 bg-muted p-3 font-mono text-[10px] leading-relaxed">
                  <span className="text-muted-foreground">cert_hash:</span>{" "}
                  0x9f3a7d1c4b8e6a2f5d9c1e8b4a7f2d6c3b9e5a8d1f4c7b2e6a9d3c5b8e1f4a7d
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Block #51,284,917
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center bg-foreground">
                    <Hash className="h-4 w-4 text-background" />
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 -z-10 h-full w-full border-2 border-primary"></div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Trust strip ============ */}
      <section className="border-b border-foreground bg-foreground py-5 text-background">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6 mono text-[10px] uppercase tracking-widest">
          <span>SHA-256 anchored</span>
          <span className="text-background/40">/</span>
          <span>Polygon-compatible</span>
          <span className="text-background/40">/</span>
          <span>NFT credentials</span>
          <span className="text-background/40">/</span>
          <span>Decentralised Identity (DID)</span>
          <span className="text-background/40">/</span>
          <span>AI Tutor: GPT · Claude · Gemini</span>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section id="features" className="border-b border-foreground">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-4">
              <div className="mono mb-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                /01 — The Stack
              </div>
              <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
                A complete academic operating system.
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-foreground/80">
                Built as one cohesive platform with strict role-based access for
                students, teachers and administrators — no patched-together
                tools.
              </p>
            </div>

            <div className="md:col-span-8">
              <div className="grid grid-cols-1 gap-px bg-foreground sm:grid-cols-2">
                <FeatureBlock
                  icon={<BookOpenCheck className="h-5 w-5" />}
                  title="LMS Core"
                  text="Courses, modules, video lessons, downloadable PDFs, auto-graded quizzes & exams."
                />
                <FeatureBlock
                  icon={<ShieldCheck className="h-5 w-5" />}
                  title="Blockchain Certificates"
                  text="Tamper-proof SHA-256 anchored credentials with public QR-based verification."
                />
                <FeatureBlock
                  icon={<Cpu className="h-5 w-5" />}
                  title="AI Tutor"
                  text="Switch between GPT-5.2, Claude Sonnet 4.5 and Gemini 3 Flash — 24/7 help."
                />
                <FeatureBlock
                  icon={<KeyRound className="h-5 w-5" />}
                  title="DID Identity"
                  text="Lifelong learning identity that travels across employers and institutions."
                />
                <FeatureBlock
                  icon={<Layers className="h-5 w-5" />}
                  title="Skills Passport"
                  text="Verified micro-credentials, gradebook entries and capstone achievements."
                />
                <FeatureBlock
                  icon={<Sparkles className="h-5 w-5" />}
                  title="Wallet + Bursaries"
                  text="Simulated student wallet, course payments, and smart-contract scholarships."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Mowanzi Coins (Earn while you learn) ============ */}
      <section className="border-b border-foreground bg-foreground text-background">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="mono mb-3 inline-flex items-center gap-2 border border-background/30 bg-background/10 px-3 py-1.5 text-[10px] uppercase tracking-widest text-background">
                <span className="h-1.5 w-1.5 rounded-full bg-accent"></span>
                Native reward currency
              </div>
              <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tighter sm:text-5xl">
                Earn{" "}
                <span className="bg-primary px-2 text-primary-foreground">Mowanzi</span>{" "}
                while you learn.
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-background/80">
                Most platforms charge you to learn. Growthink <em>pays</em> you. Every module, course and streak earns Mowanzi (MWZ) coins — spendable on books, hardware, premium AI tutor, hackathon tickets, career coaching and even tuition discounts.
              </p>

              <div className="mt-8 space-y-3">
                <EarnLine icon={<Sparkles className="h-5 w-5" />} label="Sign up" reward="+500 MWZ" />
                <EarnLine icon={<Flame className="h-5 w-5" />} label="Every module you complete" reward="+1,000 MWZ" />
                <EarnLine icon={<Trophy className="h-5 w-5" />} label="Every course you complete" reward="+5,000 MWZ bonus" />
                <EarnLine icon={<Coins className="h-5 w-5" />} label="Daily learning streak" reward="+50 MWZ / day" />
              </div>

              <Link to="/login" className="mt-10 inline-block">
                <Button
                  size="lg"
                  className="rounded-none bg-primary text-primary-foreground hard-shadow-mint hover-lift"
                  data-testid="cta-mwz-signup"
                >
                  Claim 500 MWZ · Sign up →
                </Button>
              </Link>
            </div>

            <div className="md:col-span-7">
              <div className="relative">
                {/* MWZ coin showcase */}
                <div className="border-2 border-background bg-background p-7 text-foreground">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Mowanzi Wallet · MWZ
                      </div>
                      <div className="mt-2 flex items-baseline gap-2">
                        <div className="font-display text-6xl font-extrabold tracking-tighter">
                          6,500
                        </div>
                        <div className="mono text-base font-bold text-primary">MWZ</div>
                      </div>
                      <div className="mono mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                        Aria Mokoena · streak day 12
                      </div>
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center bg-primary text-primary-foreground">
                      <Coins className="h-6 w-6" />
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-3">
                    <MiniStat label="Modules" value="6" />
                    <MiniStat label="Courses" value="1" />
                    <MiniStat label="Streak" value="12d" />
                  </div>

                  <div className="mt-6 border-t border-border pt-4">
                    <div className="mono mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                      Recent earnings
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <RowL desc="Course complete · Blockchain Fundamentals" amt="+5,000" />
                      <RowL desc="Module complete · Cryptographic Foundations" amt="+1,000" />
                      <RowL desc="Module complete · Consensus Mechanisms" amt="+1,000" />
                      <RowL desc="Daily streak · day 12" amt="+50" />
                    </div>
                  </div>
                </div>

                {/* Floating store sticker */}
                <div className="absolute -bottom-6 -right-6 hidden border-2 border-background bg-accent p-4 text-accent-foreground hard-shadow md:block">
                  <ShoppingBag className="mb-2 h-5 w-5" />
                  <div className="mono text-[10px] uppercase tracking-widest">
                    Spend on
                  </div>
                  <div className="font-display text-base font-extrabold leading-tight">
                    Books, GPUs,<br />Hoodies & more
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Roles ============ */}
      <section className="border-b border-foreground bg-muted/60">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mono mb-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            /02 — Built for three personas
          </div>
          <h2 className="font-display max-w-3xl text-4xl font-extrabold tracking-tighter sm:text-5xl">
            One platform. Three dashboards. Zero compromise.
          </h2>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            <RoleCard
              label="Student"
              color="primary"
              points={[
                "Personal academic wallet & progress",
                "NFT certificates & skills passport",
                "AI Tutor on every lesson",
                "Quiz & exam history",
              ]}
            />
            <RoleCard
              label="Teacher"
              color="accent"
              points={[
                "Author courses, modules & quizzes",
                "Live gradebook across cohorts",
                "Issue verified micro-credentials",
                "Track at-risk students",
              ]}
            />
            <RoleCard
              label="Administrator"
              color="foreground"
              points={[
                "System-wide analytics & revenue",
                "User management & permissions",
                "Immutable on-chain audit trail",
                "Course catalogue oversight",
              ]}
            />
          </div>
        </div>
      </section>

      {/* ============ How verification works ============ */}
      <section className="border-b border-foreground">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 md:grid-cols-2">
          <div>
            <div className="mono mb-3 text-[10px] uppercase tracking-widest text-muted-foreground">
              /03 — Trust by default
            </div>
            <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
              Every credential is publicly verifiable.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-foreground/80">
              Employers and governments can verify any Growthink credential in
              under 3 seconds — no account, no fee, no friction. Just paste the
              hash.
            </p>
            <Link to="/verify" className="mt-6 inline-block">
              <Button
                size="lg"
                className="rounded-none bg-foreground text-background hard-shadow hover-lift"
                data-testid="cta-verify-section"
              >
                Open Public Verify Portal →
              </Button>
            </Link>
          </div>
          <div className="border-2 border-foreground bg-background p-6 hard-shadow">
            <div className="mono mb-3 text-[10px] uppercase tracking-widest text-muted-foreground">
              Sample audit record
            </div>
            <pre className="overflow-x-auto bg-muted p-4 font-mono text-xs leading-relaxed">{`{
  "event": "certificate_issued",
  "subject": "Aria Mokoena → Blockchain Fundamentals",
  "issued_at": "2026-02-14T09:42:18Z",
  "cert_hash": "0x9f3a7d1c4b8e6a2f5d9c1e8b4a7f2d6c…",
  "txn_hash":  "0x1c0b2e9c5a8e6f3d…",
  "network": "polygon (simulated)",
  "block": 51284917,
  "valid": true
}`}</pre>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FeatureBlock({ icon, title, text }) {
  return (
    <div className="bg-background p-6">
      <div className="flex h-10 w-10 items-center justify-center bg-foreground text-background">
        {icon}
      </div>
      <div className="mt-4 font-display text-lg font-extrabold tracking-tight">
        {title}
      </div>
      <p className="mt-2 text-sm leading-relaxed text-foreground/80">{text}</p>
    </div>
  );
}

function RoleCard({ label, color, points }) {
  const bg =
    color === "primary"
      ? "bg-primary text-primary-foreground"
      : color === "accent"
      ? "bg-accent text-accent-foreground"
      : "bg-foreground text-background";
  return (
    <div className="border-2 border-foreground bg-background p-6 hover-lift">
      <div className={`${bg} mono mb-5 inline-block px-2 py-1 text-[10px] uppercase tracking-widest`}>
        {label}
      </div>
      <ul className="space-y-3 text-sm">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-foreground"></span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EarnLine({ icon, label, reward }) {
  return (
    <div className="flex items-center justify-between border-b border-background/15 pb-3">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
          {icon}
        </div>
        <span>{label}</span>
      </div>
      <span className="mono font-mono text-sm font-bold text-accent">{reward}</span>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-border bg-muted p-2.5">
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className="font-display mt-1 text-xl font-extrabold tracking-tight">
        {value}
      </div>
    </div>
  );
}

function RowL({ desc, amt }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="truncate text-muted-foreground">{desc}</span>
      <span className="mono font-mono font-semibold text-accent">{amt}</span>
    </div>
  );
}
